import * as React from 'react';
import { useState, useMemo } from 'react';
import * as API from '../Utils/API.js';
import { Copilot } from '../Types/Copilot';
import { Package, PackageAddOn, PackageCustomQuestion, PackageCustomQuestionFieldType, PackageTier } from '../Types/Package'
import { PaymentMethod, formatCardBrand, formatCardFunding } from '../Types/PaymentMethod';
import { DismissibleModal } from '../Components/Modal';
import { htmlIf, maybeHtml } from '../Utils/HTML';
import { formatUSDWithCents } from '../Utils/Currency';
import { GiftCardEntry } from './Components/GiftCardEntry';

type Props =
  { platformFeePercent: number
  , paymentMethods: PaymentMethod[]
  , utmLcSource?: string
  , package: Package
  , tier: PackageTier
  , creditValidationKey?: string
  }

const termsAndConditionsPath = '/terms-of-service'
const agentRequestsPath = '/agent/requests'
const agentDashboardPath = '/agent'

const PackagePurchase = (props: Props) => {
  const isHourlyPackage = props.package.hourlyRateCents !== undefined && props.package.hourlyRateCents !== null

  const [quantity, setQuantity] = useState(1);
  const [agentNotes, setAgentNotes] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [selectedAddOnIds, setSelectedAddOnIds] = useState<Set<number>>(new Set());

  const availableAddOns = useMemo(
    () => !isHourlyPackage ? (props.package.addOns || []) : [],
    [isHourlyPackage, props.package.addOns]
  );

  function toggleAddOn(addOnId: number) {
    setSelectedAddOnIds((prev) => {
      const next = new Set(prev);
      if (next.has(addOnId)) {
        next.delete(addOnId);
      } else {
        next.add(addOnId);
      }
      return next;
    });
  }

  function addOnTotalCents(): number {
    return availableAddOns
      .filter((addOn) => selectedAddOnIds.has(addOn.id))
      .reduce((sum, addOn) => sum + addOn.amountCents, 0);
  }

  type CustomQuestionAnswer = string | number | boolean | null;
  type CustomQuestionAnswersMap = Record<number, CustomQuestionAnswer>
  const [customQuestionAnswers, setCustomQuestionAnswers] = useState<CustomQuestionAnswersMap>({}) ;

  const availablePaymentMethods = props.paymentMethods;
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>(props.paymentMethods[0]?.id);
  const [showAddPaymentMethodModal, setShowAddPaymentModal] = useState(false);
  const [creditCodeCode, setCreditCodeCode] = useState<string>();

  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [submitRequestError, setSubmitRequestError] = useState<string>();

  function packagePriceCents(): number {
    switch (props.tier) {
      case PackageTier.BASIC:
        return (props.package.basicAmountCents ?? 0) * quantity
      case PackageTier.STANDARD:
        return (props.package.standardAmountCents ?? 0) * quantity
      case PackageTier.PREMIUM:
        return (props.package.premiumAmountCents ?? 0) * quantity
    }
  }

  function packageTierName(): string {
    switch (props.tier) {
      case PackageTier.BASIC:
        return "Basic"
      case PackageTier.STANDARD:
        return "Standard"
      case PackageTier.PREMIUM:
        return "Premium"
    }
  }

  function submitRequest() {
    if (!agreeTerms) {
      setShowErrors(true);
    } else {
      setIsSubmittingRequest(true);

      const postBody = {
        packageId: props.package.id,
        tier: props.tier,
        quantity: quantity,
        agentNotes: agentNotes,
        paymentMethodId: selectedPaymentMethodId,
        creditCodeCode: creditCodeCode,
        utmLcSource: props.utmLcSource,
        customQuestionAnswers: customQuestionAnswers,
        addOnIds: Array.from(selectedAddOnIds)
      }

      API.post("agent_packages_submit_package_purchase_path", postBody).then(function (result) {
        setIsSubmittingRequest(false);

        if (result['error'] === true) {
          setSubmitRequestError(
            "Something went wrong. Our team has been notified and will investigate the issue. If you continue to experience problems purchasing a package, please email us at hello@letslucia.com"
          )
        } else if (result['success'] === true) {
          window.location.href = agentRequestsPath
        }
      })
    }
  }

  function subtotalCents(): number {
    return packagePriceCents() + addOnTotalCents()
  }

  function platformFeeAmountCents(): number {
    return Math.floor(subtotalCents() * props.platformFeePercent * 0.01)
  }

  function totalAmountCents(): number {
    return (subtotalCents() + platformFeeAmountCents())
  }

  const handleAgreeTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeTerms(event.target.checked);
  };

  function addNewPaymentMethod() {
    API.post("agent_account_add_new_payment_method_path", {
      returnTo: window.location.pathname + window.location.search
    }).then(function (result) {
      window.location.href = result['url'];
    })
  }

  const ViewAddPaymentMethodModal = () => {
    return (
      <DismissibleModal
        title={
          <h4>Add a new payment method</h4>
        }
        body={
          <div>
            You’ll be securely taken to Stripe to enter your card details, then brought right back here
            to finish your purchase.
          </div>
        }
        footer={
          <div className="text-start">
            <button className="btn btn-primary w-100 w-sm-auto mb-2 mb-sm-0"
              onClick={addNewPaymentMethod}
            >
              Continue to add card
            </button>
            <button className="btn btn-outline-success w-100 w-sm-auto ms-sm-2"
              onClick={() => setShowAddPaymentModal(false)}
            >
              Cancel
            </button>
          </div>
        }
        onDismiss={() => setShowAddPaymentModal(false)}
      />
    )
  }

  return (
    <div>
      { htmlIf(showAddPaymentMethodModal, <ViewAddPaymentMethodModal />)}
      <div className="card">
        <div className="card-body">
          <h4 className="fw-bold">Confirm & Pay</h4>
          <div className="mt-2">
            <h5 className="mb-0">{props.package.title}</h5>
            <div className="fs-sm text-uppercase text-success fw-bolder">{packageTierName()}</div>
            {maybeHtml(props.package.copilotIndividual, (copilot) =>
              <div className="d-flex align-items-center mt-2">
                <div className="avatar avatar-sm">
                  <img className="avatar-img rounded-circle" src={copilot.imageUrl}/>
                </div>
                <div className="ms-1">{copilot.preferredName}</div>
              </div>
            )}
            <div className="d-md-flex mt-2">
              {htmlIf(!isHourlyPackage,
                <div className="fs-md d-flex align-items-center me-md-3">
                  Quantity:
                  <select className="form-select form-select-sm ms-1"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                  >
                    {Array.from({ length: 20 }, (_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="fs-md d-flex align-items-center">
                Request type:
                <span className="text-dark ms-1 fw-bold">
                  { isHourlyPackage ? "Hourly rate" : "Fixed rate project" }
                </span>
              </div>
              <div className="fs-md d-flex align-items-center ms-md-3">
                { isHourlyPackage ? "Rate" : "Price" }
                <span className="text-dark ms-1 fw-bold">
                  { isHourlyPackage
                  ? formatUSDWithCents(props.package.hourlyRateCents)
                  : formatUSDWithCents(packagePriceCents())
                  }
                </span>
              </div>
            </div>
          </div>
          {htmlIf(availableAddOns.length > 0,
            <div className="mt-3">
              <h6 className="fw-bold mb-2">Add-ons</h6>
              {availableAddOns.map((addOn) => (
                <div className="form-check mb-1" key={addOn.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`addon-${addOn.id}`}
                    checked={selectedAddOnIds.has(addOn.id)}
                    onChange={() => toggleAddOn(addOn.id)}
                  />
                  <label className="form-check-label d-flex justify-content-between" htmlFor={`addon-${addOn.id}`}>
                    <span>{addOn.name}</span>
                    <span className="text-dark fw-semibold ms-2">{formatUSDWithCents(addOn.amountCents)}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
          {htmlIf(!isHourlyPackage,
            <div>
              <hr className="mt-3" />
              <div className="mt-3 fs-md">
                <div className="row">
                  <div className="col">{packageTierName()} package{quantity > 1 ? ` (x${quantity})` : ''}:</div>
                  <div className="col-auto text-end">{formatUSDWithCents(packagePriceCents())}</div>
                </div>
                {availableAddOns.filter((a) => selectedAddOnIds.has(a.id)).map((addOn) => (
                  <div className="row" key={addOn.id}>
                    <div className="col">{addOn.name}:</div>
                    <div className="col-auto text-end">{formatUSDWithCents(addOn.amountCents)}</div>
                  </div>
                ))}
                {htmlIf(props.platformFeePercent > 0,
                  <div className="row">
                    <div className="col">Service Fee:</div>
                    <div className="col-auto text-end">{formatUSDWithCents(platformFeeAmountCents())}</div>
                  </div>
                )}
              </div>
              <hr className="mt-2" />
              <div className="mt-2 fs-xl text-dark">
                <div className="row">
                  <div className="col">Total:</div>
                  <div className="col-auto text-end fw-bold">{formatUSDWithCents(totalAmountCents())}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="card mt-2">
        <div className="card-body">
          <h4 className="fw-bold">Additional Information</h4>
          {props.package.customQuestions.map((question) => (
            <div className="mb-3" key={question.id}>
              <div className="fs-md mb-1">{question.questionText}{question.isRequired && '*'}</div>
              <CustomQuestionField
                question={question}
                value={customQuestionAnswers[question.id] ?? ''}
                onChange={(value) => setCustomQuestionAnswers((prev) => ({ ...prev, [question.id]: value }))}
              />
            </div>
          ))}
          <div className="fs-md">
            Share any additional details that may be helpful to your CoPilot.
            <textarea
              className="form-control mt-2 t--package-agent-notes"
              rows={5}
              placeholder="Add some details…"
              value={agentNotes}
              onChange={(event) => setAgentNotes(event.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="card mt-2">
        <div className="card-body">
          <h4 className="fw-bold">Payment</h4>
          <div className="card rounded-2">
            <div className="card-header p-2 mx-0 w-100 text-dark fw-bold">
              Saved payment options
            </div>
            <div className="card-body p-2">
              {htmlIf(availablePaymentMethods.length === 0,
                <div className="fs-sm text-secondary mb-0">
                  No saved cards yet. Add a credit or debit card below to continue.
                </div>
              )}
              {availablePaymentMethods.map((paymentMethod, index) => (
                <div className={`form-check ${index === availablePaymentMethods.length - 1 ? 'mb-0' : 'mb-1'}`} key={paymentMethod.id}>
                  <input
                    className="form-check-input"
                    type="radio"
                    id={paymentMethod.id}
                    name="pm-radio"
                    value={paymentMethod.id}
                    onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                    checked={selectedPaymentMethodId === paymentMethod.id}
                    />
                  <label className="form-check-label" htmlFor={paymentMethod.id}>
                    <b>{formatCardBrand(paymentMethod.card.brand)} •••• {paymentMethod.card.last4}</b>
                    <br />
                    expiring {paymentMethod.card.expiration}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="card rounded-2 mt-3">
            <div className="cursor-pointer d-flex card-body p-2 justify-content-between align-items-center fs-md text-dark fw-bold"
              onClick={() => setShowAddPaymentModal(true)}
            >
              <div>Add credit/debit card</div>
              <i className="ai-chevron-right" />
            </div>
          </div>
          {htmlIf(!isHourlyPackage,
            <GiftCardEntry creditCodeCodeSetter={setCreditCodeCode} validationKey={props.creditValidationKey}/>
          )}
          <div className="mt-3 fs-sm">
            Your personal information will be used to process your order, to
            support your experience on this site and for other purposes described
            in the{" "}
            <a href="/terms-of-service">privacy policy</a>.
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input t--agree-terms"
              type="checkbox" id="agree-terms" checked={agreeTerms} onChange={handleAgreeTermsChange}
            />
            <label className="form-check-label" htmlFor="agree-terms">
              I agree to the <a href={termsAndConditionsPath} target="_blank">Terms of Service</a>
            </label>
          </div>
          {htmlIf((showErrors && !agreeTerms),
            <div className="fs-sm mt-1 text-danger fw-bold">Please agree to the Terms of Service.</div>
          )}
          <div className="mt-3">
            <button
              className="btn btn-success px-md-6 t--submit-package-purchase"
              onClick={submitRequest}
              disabled={isSubmittingRequest || !selectedPaymentMethodId}
            >
              Confirm & Pay
            </button>
            {htmlIf(!selectedPaymentMethodId,
              <div className="fs-sm mt-2 text-secondary">Add a payment method above to complete your purchase.</div>
            )}
          </div>
          {htmlIf(submitRequestError !== null,
            <div className="mt-2 text-danger fw-bold">{submitRequestError}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PackagePurchase;

type CustomQuestionProps =
  { question: PackageCustomQuestion
  , value: string | number | boolean
  , onChange: (value: string | number | boolean | null) => void
  }

const CustomQuestionField = ({ question, value, onChange}: CustomQuestionProps) => {
  switch (question.fieldType) {
    case PackageCustomQuestionFieldType.SHORT_TEXT:
      return (
        <input
          className="form-control"
          placeholder={question.placeholderText}
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case PackageCustomQuestionFieldType.LONG_TEXT:
      return (
        <textarea
          className="form-control"
          rows={5}
          placeholder={question.placeholderText}
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case PackageCustomQuestionFieldType.INTEGER:
      return (
        <input
          className="form-control"
          type="number" step={1} pattern="\d+"
          placeholder={question.placeholderText}
          value={value as string}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
        />
      );

    case PackageCustomQuestionFieldType.DROPDOWN:
      return (
        <select
          className="form-select"
          value={value as string}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select an option</option>
          {question.dropdownOptions.map((opt, i) => (
            <option key={i} value={opt}>{opt}</option>
          ))}
        </select>
      );

    case PackageCustomQuestionFieldType.BOOLEAN:
      return (
        <input
          className="form-check-input"
          type="checkbox"
          checked={value as boolean}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    default:
      return null;
  }
};
